// src/components/help/HelpNow.jsx
//
// Help surfaces for the toolkit and Resources, all reading the fleet's one
// seven-market crisis table (packages/crisis-support, through
// src/constants/crisisSupport.js). Never a number typed in here.
//
//   HelpNowStrip        - the short "talk to a real person" line every tool
//                         shows at the top (ToolNavigation renders it)
//   IntimateImageAdvice - what to do, and not do, about a nude or sexual
//                         image of anyone under 18, with the reporting
//                         service for the reader's country
//
// The reader's country is the one they chose (here, on Resources or in the
// Coach), else their browser's region. When it is neither, these ask "where
// are you?" instead of defaulting to Australia, and say what to do anywhere:
// call the local emergency number, or use a helpline directory.
//
// Australian English throughout.

import React, { useId, useState } from 'react';
import { LifeBuoy, ExternalLink, Phone, ShieldAlert } from 'lucide-react';
import {
  CRISIS_SUPPORT,
  COUNTRY_KEYS,
  DIRECTORIES,
  TAKE_IT_DOWN,
  emergencyCallAdvice,
  getCrisisSupport,
  getIntimateImageReporting,
  knownCountry,
  writeCountryPref,
} from '../../constants/crisisSupport';

const OTHER = 'other';
const directory = DIRECTORIES[0];

// "in the United Kingdom", "in Australia".
const inCountry = (name) => (/^United /.test(name) ? `the ${name}` : name);

// "Triple Zero (000)" dials as 000; text-only lines have no tel: link.
export const dialable = (number) => {
  const raw = String(number || '').replace(/^Triple Zero \((.*)\)$/, '$1');
  return /^[\d\s()+-]+$/.test(raw) ? raw.replace(/[^\d+]/g, '') : null;
};

/** The reader's country (or null), and a setter that remembers a real choice. */
export function useHelpCountry() {
  const [country, setCountry] = useState(() => knownCountry());
  const choose = (value) => {
    setCountry(value || null);
    if (value && value !== OTHER) writeCountryPref(value);
  };
  return [country, choose];
}

export function CountrySelect({ country, onChange, className = '', label = 'Choose your country for help services' }) {
  return (
    <select
      value={country || ''}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={`rounded border border-slate-500 bg-slate-800 px-2 py-1 text-xs text-slate-100 ${className}`}
    >
      {!country && <option value="">Where are you?</option>}
      {COUNTRY_KEYS.map((key) => (
        <option key={key} value={key}>{CRISIS_SUPPORT[key].name}</option>
      ))}
      <option value={OTHER}>Somewhere else</option>
    </select>
  );
}

const DirectoryLine = ({ className = '' }) => (
  <span className={className}>
    {emergencyCallAdvice()}{' '}
    <a
      href={`https://${directory.website}`}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 hover:text-white"
    >
      {directory.name}
    </a>{' '}
    lists free helplines in more than 175 countries.
  </span>
);

/**
 * One line, every tool: who to talk to, free, in the reader's own country.
 * Youth lines first, two at most, plus the emergency number.
 */
export function HelpNowStrip() {
  const [country, choose] = useHelpCountry();
  const data = country && country !== OTHER ? getCrisisSupport(country) : null;
  const services = data
    ? [...data.services.filter((s) => s.youthFocused), ...data.services.filter((s) => !s.youthFocused)].slice(0, 2)
    : [];

  return (
    <section
      aria-label="Free help, if you need it"
      data-help-strip={data ? country : 'none'}
      className="mb-6 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-start gap-2 text-xs leading-relaxed text-rose-100 sm:text-sm">
          <LifeBuoy className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-300" aria-hidden="true" />
          <span>
            Feeling low, unsafe, or hurt by someone? You can talk to a real person, free and confidential
            {data ? <>, in {inCountry(data.name)}:</> : '.'}
          </span>
        </p>
        <CountrySelect country={country} onChange={choose} />
      </div>
      {data ? (
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 pl-6 text-xs sm:text-sm">
          {services.map((service) => (
            <li key={service.id || service.name} className="text-rose-50">
              <span className="font-medium">{service.name}</span>{' '}
              {dialable(service.number) ? (
                <a href={`tel:${dialable(service.number)}`} className="font-semibold text-white underline underline-offset-2">
                  {service.number}
                </a>
              ) : (
                <span className="font-semibold text-white">{service.number}</span>
              )}
            </li>
          ))}
          <li className="text-rose-50">
            <span className="font-medium">Emergency</span>{' '}
            <span className="font-semibold text-white">{data.emergency.number}</span>
          </li>
        </ul>
      ) : (
        <p className="mt-1 pl-6 text-xs text-rose-100">
          <DirectoryLine />
        </p>
      )}
    </section>
  );
}

/**
 * A nude or sexual image of anyone under 18: don't capture it, tell an adult,
 * report it, with the reporting route for the reader's country.
 */
export function IntimateImageAdvice({ headingLevel = 4, className = '', country: controlledCountry, onCountryChange }) {
  const [ownCountry, chooseOwn] = useHelpCountry();
  // A page that already has a country picker passes its choice in, so the
  // reader isn't asked twice.
  const controlled = onCountryChange !== undefined;
  const country = controlled ? controlledCountry : ownCountry;
  const choose = controlled ? onCountryChange : chooseOwn;
  const headingId = useId();
  const Heading = `h${headingLevel}`;
  const reporting = country && country !== OTHER ? getIntimateImageReporting(country) : null;
  const support = country && country !== OTHER ? getCrisisSupport(country) : null;
  const youthLine = support?.services.find((s) => s.youthFocused) || support?.services[0];

  return (
    <section
      aria-labelledby={headingId}
      data-intimate-image-advice={reporting ? country : 'none'}
      className={`rounded-xl border-2 border-amber-400/60 bg-amber-500/10 p-4 sm:p-5 ${className}`}
    >
      <Heading id={headingId} className="mb-3 flex items-start gap-2 font-semibold text-amber-200">
        <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
        If it&apos;s a nude or sexual picture or video of anyone under 18
      </Heading>
      <ul className="space-y-2 text-sm leading-relaxed text-amber-50">
        <li>
          <span className="font-semibold">Don&apos;t screenshot, save, forward or show it to anyone</span>, not even
          to prove what happened. Saving or sharing a sexual image of someone under 18 can be a serious crime, even
          when you&apos;re only trying to help.
        </li>
        <li>
          Write down where it is instead: the app or site, the link, the account name and the date. That&apos;s what
          the services below need. (You can screenshot the threats or messages around it, just never the picture.)
        </li>
        <li>Tell a trusted adult: a parent, carer, teacher or school counsellor.</li>
        <li>Report it on the app or site, and to the service where you live.</li>
        <li>
          If someone is threatening you or asking for money or more pictures: don&apos;t pay, don&apos;t send
          anything, stop replying, and tell an adult and the service now. If it&apos;s a picture of you, it is not
          your fault.
        </li>
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-amber-100">
        <span>Where you are:</span>
        <CountrySelect country={country} onChange={choose} label="Choose your country for reporting services" />
      </div>

      <ul className="mt-3 space-y-3">
        {(reporting?.services || []).map((service) => (
          <li key={service.name} className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
            <a
              href={`https://${service.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-white underline underline-offset-2"
            >
              {service.name}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <p className="mt-1 text-sm text-slate-200">{service.detail}</p>
            <p className="mt-1 text-xs text-slate-400">{service.website}</p>
          </li>
        ))}
        <li className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
          <a
            href={`https://${TAKE_IT_DOWN.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-white underline underline-offset-2"
          >
            {TAKE_IT_DOWN.name}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
          <p className="mt-1 text-sm text-slate-200">{TAKE_IT_DOWN.detail}</p>
          <p className="mt-1 text-xs text-slate-400">{TAKE_IT_DOWN.website}</p>
        </li>
      </ul>

      {youthLine ? (
        <p className="mt-3 flex items-start gap-2 text-sm text-amber-100">
          <Phone className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            To talk it through first, free and confidential: {youthLine.name}{' '}
            {dialable(youthLine.number) ? (
              <a href={`tel:${dialable(youthLine.number)}`} className="font-semibold text-white underline underline-offset-2">
                {youthLine.number}
              </a>
            ) : (
              <span className="font-semibold text-white">{youthLine.number}</span>
            )}
            . In an emergency, {support.emergency.number}.
          </span>
        </p>
      ) : (
        <p className="mt-3 text-sm text-amber-100">
          <DirectoryLine />
        </p>
      )}
    </section>
  );
}

export default HelpNowStrip;
